import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import TwoFactorStep from '@/components/TwoFactorStep';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Send } from 'lucide-react';

const LOGO_URL = 'https://customer-assets-m6fa6gv7.emergentagent.net/job_crypto-remit-1/artifacts/hfmyqknz_ChatGPT%20Image%20Sep%2016%2C%202026%2C%2002_56_22%20PM.png';

export default function MboaSendLogin() {
  const { login, loginWith2fa, user } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      const role = user.role;
      if (role === 'admin' || role === 'super_admin') navigate('/admin', { replace: true });
      else if (role === 'agent') navigate('/agent', { replace: true });
      else navigate('/send/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Remplissez tous les champs'); return; }
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.requires_2fa) { setChallenge(result.challenge_token); setLoading(false); return; }
      afterLogin(result);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Email ou mot de passe incorrect');
    }
    setLoading(false);
  };

  const afterLogin = (result) => {
      toast.success('Connexion réussie');
      const role = result?.user?.role;
      if (role === 'admin' || role === 'super_admin') {
        navigate('/admin', { replace: true });
      } else if (role === 'agent') {
        navigate('/agent', { replace: true });
      } else {
        navigate('/send/dashboard', { replace: true });
      }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F5] flex items-center justify-center p-4" data-testid="mboasend-login">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src={LOGO_URL} alt="MBOA" className="w-10 h-10 rounded-lg" />
            <div>
              <span className="text-[#006400] text-xl font-bold" style={{ fontFamily: 'Outfit' }}>MBOA</span>
              <span className="text-[#0D2612] text-xl font-light">Send</span>
            </div>
          </div>
          <p className="text-sm text-slate-600">Envoyez de l'argent du Canada vers le Cameroun</p>
        </div>

        {challenge ? (
          <TwoFactorStep onCancel={() => setChallenge(null)}
            onSubmit={async (code) => { const r = await loginWith2fa(challenge, code); afterLogin(r); }} />
        ) : (
        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 shadow-sm rounded-2xl p-6 space-y-4">
          <div>
            <Label className="text-slate-700 text-sm">Email</Label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="votre@email.com" className="bg-white border-slate-300 text-[#0D2612] h-11 mt-1" />
          </div>
          <div>
            <Label className="text-slate-700 text-sm">Mot de passe</Label>
            <div className="relative mt-1">
              <Input type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                placeholder="••••••••" className="bg-white border-slate-300 text-[#0D2612] h-11 pr-10" />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-3 text-slate-500">
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="flex justify-end">
            <Link to="/forgot-password" className="text-[10px] text-[#006400] hover:underline">Mot de passe oublié ?</Link>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-[#006400] text-white hover:bg-[#004D00] font-semibold h-11">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Se connecter
          </Button>
        </form>
        )}

        <p className="text-center text-sm text-slate-600">
          Pas encore de compte ? <Link to="/mboasend/register" className="text-[#006400] font-semibold hover:underline">S'inscrire</Link>
        </p>

        <div className="flex flex-wrap justify-center gap-2 text-[9px] text-slate-400">
          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600">Confidentialité</a>
          <span>|</span>
          <a href="/terms-of-use" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600">CGU</a>
          <span>|</span>
          <a href="/aml-kyc-policy" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600">AML/KYC</a>
        </div>
      </div>
    </div>
  );
}
